import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { SubtleWaves } from "@/components/ui/waves";
import { ArrowLeft, Star, Truck, Leaf, Zap, Shield, Instagram, Play } from "lucide-react";
import { bundleProduct } from "@/data/products";
import { Navbar } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { StickyCart } from "@/components/StickyCart";
import { useProducts } from "@/hooks/useProducts";
import { Seo } from "@/components/Seo";

export const ProductPage = () => {
  const { slug } = useParams();
  const [selectedSize, setSelectedSize] = useState<'25g' | '50g'>('50g');
  const [hoveredProduct, setHoveredProduct] = useState<number | null>(null);
  const { products, isLoading } = useProducts();
  
  const currentProduct = products.find(p => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-muted-foreground">Product laden...</p>
        </div>
      </div>
    );
  }

  if (!currentProduct) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-serif font-bold text-foreground mb-4">Product niet gevonden</h1>
          <Link to="/" className="text-gold hover:underline">
            Terug naar home
          </Link>
        </div>
      </div>
    );
  }

  const currentPrice = selectedSize === '25g' ? currentProduct.price25g : currentProduct.price50g;
  
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: currentProduct.name,
    description: currentProduct.description,
    image: currentProduct.productImage,
    brand: { "@type": "Brand", name: "Lunssence" },
    offers: {
      "@type": "Offer",
      price: currentPrice.replace(/[€\s]/g, "").replace(",", "."),
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: `https://lunssence.com/producten/${currentProduct.slug}`,
    },
  };

  return (
    <div className="min-h-screen luxury-bg">
      <Seo
        title={`${currentProduct.name} — Lunssence`}
        description={currentProduct.seoDescription || currentProduct.description.slice(0, 155)}
        path={`/producten/${currentProduct.slug}`}
        type="product"
        image={currentProduct.productImage}
        jsonLd={productJsonLd}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <SubtleWaves />
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
            <Link to="/" className="hover:text-gold transition-colors">Home</Link>
            <span>/</span>
            <Link to="/#blends" className="hover:text-gold transition-colors">Producten</Link>
            <span>/</span>
            <span className="text-foreground">{currentProduct.name}</span>
          </div>

          {/* Back Button */}
          <Link 
            to="/#blends" 
            className="inline-flex items-center gap-2 text-gold hover:text-gold-light transition-colors mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Terug naar alle producten
          </Link>

          {/* Page Header */}
          <div className="text-center mb-16">
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-gold mb-6 text-glow">
              Premium Blends Collectie
            </h1>
            <div className="w-20 h-1 bg-gradient-gold mx-auto mb-8 rounded-full shadow-gold" />
            <p className="text-lg text-foreground max-w-2xl mx-auto leading-relaxed">
              Luxueuze thee blends geïnspireerd door de kracht van de blauwe lotus. 
              Elk product is zorgvuldig samengesteld voor jouw welzijn en balans.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {products.map((product) => {
              const IconComponent = product.icon;
              const isCurrentProduct = product.id === currentProduct.id;
              
              return (
                <Card 
                  key={product.id}
                  className={`group relative overflow-hidden transition-all duration-500 ${
                    isCurrentProduct 
                      ? 'ring-2 ring-gold shadow-gold-glow scale-105' 
                      : 'hover:shadow-[0_8px_25px_rgb(12,43,35,0.15)] hover:scale-105'
                  } ${hoveredProduct === product.id ? product.glowColor : ''}`}
                  onMouseEnter={() => setHoveredProduct(product.id)}
                  onMouseLeave={() => setHoveredProduct(null)}
                >
                  {/* Popular/Best Deal Labels */}
                  {product.isPopular && (
                    <Badge className="absolute top-4 right-4 z-20 bg-gold text-primary font-semibold">
                      Meest gekozen
                    </Badge>
                  )}

                  {/* Accent Background */}
                  <div className={`absolute top-0 right-0 w-24 h-24 ${product.accentColor} rounded-2xl opacity-20 transform rotate-12 group-hover:rotate-6 transition-transform duration-500`} />

                  <CardContent className="p-8">
                    {/* Icon */}
                    <div className={`w-16 h-16 ${product.accentColor} rounded-full flex items-center justify-center mb-6 relative z-10`}>
                      <IconComponent className="w-8 h-8 text-primary" />
                    </div>

                    {/* Product Image */}
                    <div className="relative mb-6 overflow-hidden rounded-xl">
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-all duration-700">
                        <img
                          src={product.lifestyleImage}
                          alt={`${product.name} lifestyle`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23f0f0f0' width='400' height='400'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='24' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3EGeen afbeelding%3C/text%3E%3C/svg%3E";
                          }}
                        />
                      </div>
                      <img
                        src={product.productImage}
                        alt={product.name}
                        className="relative z-10 w-full h-48 object-cover rounded-xl group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23f0f0f0' width='400' height='400'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='24' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3EGeen afbeelding%3C/text%3E%3C/svg%3E";
                        }}
                      />
                      <div className={`absolute inset-0 ${product.accentColor} rounded-xl opacity-10 group-hover:opacity-20 transition-opacity duration-300`} />
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <h3 className="font-serif text-2xl font-black text-foreground mb-3 leading-tight">
                        {product.name}
                      </h3>
                      
                      <p className="text-lg font-light italic text-gold/80 mb-6 tracking-wide leading-relaxed">
                        {product.tagline}
                      </p>

                      {/* Key Benefits */}
                      <ul className="space-y-3 mb-8">
                        {product.bullets.map((bullet, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-base font-medium text-foreground leading-relaxed">
                            <span className="w-2 h-2 bg-gold rounded-full mt-2.5 flex-shrink-0 shadow-gold" />
                            {bullet}
                          </li>
                        ))}
                      </ul>

                      {/* Pricing */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">25g</span>
                          <span className="text-xl font-bold text-gold">{product.price25g}</span>
                          <span className="text-muted-foreground">|</span>
                          <span className="text-sm text-muted-foreground">50g</span>
                          <span className="text-2xl font-bold text-gold text-glow">{product.price50g}</span>
                        </div>
                        {product.isPopular && (
                          <Badge variant="secondary" className="bg-gold/20 text-gold">
                            50g Populair
                          </Badge>
                        )}
                      </div>

                      {/* CTA Button */}
                      <Link to={`/producten/${product.slug}`}>
                        <Button 
                          className={`w-full btn-hero ${
                            isCurrentProduct 
                              ? 'animate-zen-glow ring-2 ring-gold' 
                              : ''
                          }`}
                        >
                          {isCurrentProduct ? 'Huidige selectie' : 'Begin jouw ritueel'}
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Bundle Spotlight */}
          <Card className="relative overflow-hidden p-8 md:p-12 text-center bg-gradient-to-r from-card to-muted/30 border-gold/20 shadow-gold-glow">
            <Badge className="absolute top-6 right-6 bg-destructive text-white text-lg px-4 py-2">
              Beste Deal
            </Badge>
            
            <div className="max-w-3xl mx-auto">
              <h3 className="font-serif text-3xl md:text-4xl font-bold text-gold mb-6 text-glow">
                {bundleProduct.name}
              </h3>
              
              <p className="text-lg text-foreground mb-8 leading-relaxed">
                {bundleProduct.description}
              </p>

              {/* Bundle Visualization */}
              <div className="flex justify-center items-center gap-4 mb-8 overflow-x-auto">
                {products.map((product, index) => (
                  <div key={product.id} className="relative flex-shrink-0">
                    <img
                      src={product.productImage}
                      alt={product.name}
                      className="w-20 h-24 object-cover rounded-lg shadow-md hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gold/10 rounded-lg" />
                    {index < products.length - 1 && (
                      <div className="absolute -right-6 top-1/2 -translate-y-1/2 text-gold font-bold text-xl">+</div>
                    )}
                  </div>
                ))}
                <div className="text-gold font-bold text-2xl mx-4">=</div>
                <Badge className="bg-destructive text-white text-lg px-4 py-2 flex-shrink-0">
                  -{bundleProduct.discount}
                </Badge>
              </div>

              {/* Bundle Pricing */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
                <div className="text-center">
                  <span className="text-sm text-muted-foreground line-through block">{bundleProduct.originalPrice}</span>
                  <span className="text-4xl font-bold text-gold">{bundleProduct.price}</span>
                  <span className="text-sm text-destructive font-semibold ml-2">Bespaar {bundleProduct.savings}</span>
                </div>
              </div>

              <Button className="btn-hero text-lg px-12 py-6 animate-zen-glow">
                Complete collectie bestellen
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              Deel jouw #LunssenceLife
            </h2>
            <p className="text-lg text-muted-foreground">
              Word onderdeel van onze community en deel jouw rituelen
            </p>
          </div>

          {/* Mini Social Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[1,2,3,4].map((item) => (
              <div key={item} className="relative aspect-square rounded-lg overflow-hidden group cursor-pointer">
                <img 
                  src={products[item % 3].lifestyleImage}
                  alt={`Lunssence lifestyle ${item}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <Play className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button variant="outline" className="btn-secondary">
              <Instagram className="w-5 h-5 mr-2" />
              Volg @lunssence
            </Button>
          </div>
        </div>
      </section>

      {/* Trust Icons */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Truck, title: "Gratis verzending", subtitle: "Vanaf €50" },
              { icon: Leaf, title: "100% Natuurlijk", subtitle: "Biologische ingrediënten" },
              { icon: Zap, title: "Snelle levering", subtitle: "1-2 werkdagen" },
              { icon: Shield, title: "Premium kwaliteit", subtitle: "Biologisch gecertificeerd" }
            ].map((trust, index) => {
              const IconComponent = trust.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gold/30 transition-colors duration-300">
                    <IconComponent className="w-8 h-8 text-gold" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{trust.title}</h3>
                  <p className="text-sm text-muted-foreground">{trust.subtitle}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
      <StickyCart />
    </div>
  );
};